<template>
  <div class="document-keywords-body">
    <div class="keywords-list">
      <KeywordsPhoneCategory
        v-if="isPhone"
        :category="category"
        :keywords="keywords"
        :limit="itemsShown"
        @add="onKeywordsAdd"
        @edit-category="onCategoryEdit"
        @delete-category="onCategoryDelete"
        @edit-keyword="onKeywordsEdit"
        @delete-keywords="onPhoneDelete"
      />
      <template v-else>
        <div class="title">
          <div class="category-color" :style="{ background: category.colorCode }"></div>
          {{ category.name }}
          <div class="keywords-list__buttons">
            <BaseRoundButton
              @click.native="onCategoryEdit"
              :size="40"
              :tooltip-text="$t('actions.edit')"
              class="keywords-list__button"
              icon="edit-2"
              is-white
            />
            <BaseRoundButton
              @click.native="onCategoryDelete"
              :size="40"
              :tooltip-text="$t('actions.delete')"
              class="keywords-list__button"
              icon="trash-2"
              is-red
              is-white
            />
          </div>
        </div>

        <div v-if="keywords.length >= 1">
          <TableNormal
            v-model="selectedKeywords"
            :fixed-header="true"
            :headers="headers"
            :items="keywords"
            :items-per-page="itemsToShow"
            :single-select="false"
            hide-default-footer
            item-key="value"
            show-select
            class="admin-keywords-table"
          >
            <template v-slot:header.data-table-select="{}">
              <FormCheckbox
                :indeterminate="partiallySelected"
                :afterIndeterminate="false"
                :onChange="onSelectAllClick"
                :value="allAreSelected"
                class="keywords-list__check-all"
              />

              <BaseBulkActionMenu v-if="hasSelection" class="base-bulk-action-menu">
                <template slot="content">
                  <BaseRoundButton
                    @click.native="openDeleteKeywords()"
                    :tooltip-text="$t('actions.delete')"
                    class="keywords-status-manager__item"
                    icon="trash-2"
                    is-red
                    is-white
                  />
                </template>
              </BaseBulkActionMenu>
            </template>

            <template v-slot:header.actions>
              <BaseButton @click.native="onKeywordsAdd" isSmall class="keywords-list__add-keyword" has-icon-left>
                <BaseIcon name="plus" />
                {{ $t('page.admin-keywords.add-keyword') }}
              </BaseButton>
            </template>

            <template v-slot:item.data-table-select="{ isSelected, select }">
              <FormCheckbox :value="isSelected" @input="select($event)" @click.native.stop />
            </template>

            <template v-slot:item.title="{ item }">
              <Keyword>
                {{ item.value }}
              </Keyword>
            </template>

            <template v-slot:item.actions="{ item }">
              <BaseRoundButton
                :size="32"
                :tooltip-text="$t('actions.edit')"
                icon="edit-2"
                is-white
                class="keywords-list__edit-keyword"
                @click.native="onKeywordsEdit(item)"
              />
            </template>
          </TableNormal>
          <div v-if="keywords.length > itemsShown" ref="tendersBottomList">
            <span @click="toggleKeywordsList" class="keywords-list__show-all">
              {{ showAll ? $t('dialog.keywords.hide') : $t('dialog.keywords.show-more', { count: itemsLeft }) }}
              <BaseIcon v-if="showAll" name="chevron-up"></BaseIcon>
            </span>
          </div>
        </div>
        <div v-else class="keywords-list__empty-keywords">
          <BaseButton @click.native="onKeywordsAdd" class="keywords-list__empty-add-keyword" isSmall has-icon-left>
            <BaseIcon name="plus" />
            {{ $t('page.admin-keywords.add-keyword') }}
          </BaseButton>
        </div>
      </template>
      <KeywordsAddPanel
        v-if="showAddKeywordInput"
        class="keywords-list__add-input"
        :category-name="category.name"
        @close="showAddKeywordInput = false"
        @opened="focusOnInput"
      >
        <ResizeAuto>
          <template v-slot:default="{ resize }">
            <textarea
              ref="textAreaRef"
              @input="resize"
              :placeholder="$t('page.admin-keywords.type-keyword')"
              v-model="rawKeywords"
              cols="40"
              rows="1"
              class="page-keywords__textarea"
            ></textarea>
          </template>
        </ResizeAuto>
        <div v-if="tooManyKeywords" class="page-keywords__add-hint page-keywords__add-hint--error">
          {{ $t('page.admin-document-keywords.too-many-keywords', { max: maxBulkKeywords }) }}
        </div>
        <div v-else-if="toAdd.alreadyThere" class="page-keywords__add-hint">
          {{ $tc('page.admin-keywords.already-in-category', toAdd.alreadyThere, { count: toAdd.alreadyThere }) }}
        </div>
        <div class="page-keywords__add-keyword">
          <BaseShortcut
            value="~"
            isActionable
            @shortcut-clicked="addTilde()"
            v-tooltip="{ content: $t('search.tild'), placement: 'top', delay: 500 }"
          />
          <KeywordTransformButton v-model="rawKeywords" target="documents" />
          <BaseButton
            @click.native="addKeywords()"
            :disabled="!toAdd.keywords.length || tooManyKeywords"
            isSmall
            class="document-keywords__add-keyword"
          >
            {{ $tc('page.admin-keywords.add-keywords', toAdd.keywords.length, { count: toAdd.keywords.length }) }}
          </BaseButton>
        </div>
      </KeywordsAddPanel>
      <EditCategoryDialog :hasColorChoice="true" ref="editCategoryDialog" @action-succeeded="onRefresh" />
      <DeleteDialog ref="deleteCategoryDialog" @execute="onDeleteCategory" :buttonText="$t('actions.delete')"
        ><template slot="content">
          <div class="page-keywords__delete-category">
            <i18n path="page.admin-keywords.delete-dialog.content">
              <template v-slot:name>
                <span class="page-keywords__category-name"> {{ category.name }}</span>
              </template>
            </i18n>
          </div>
          <div class="page-keywords__delete-category--notice">
            {{ $t('page.admin-keywords.delete-dialog.subtitle') }}
          </div>
        </template>
      </DeleteDialog>
      <EditKeywordDialog :isDocumentKeyword="true" ref="editKeywordDialog" @action-succeeded="onRefresh" />
      <DeleteDialog ref="deleteKeywordsDialog" @execute="deleteKeywords" :buttonText="$t('actions.delete')">
        <template slot="content">
          <div class="page-keywords__delete-category">
            {{ $t('page.admin-keywords.delete-keyword') }}
            <div class="keywords-status-manager__delete-list">
              <Keyword v-for="(kw, index) in selectedKeywords" :key="index">
                {{ kw.value }}
              </Keyword>
            </div>
            <div class="page-keywords__delete-category--notice">
              {{ $t('page.admin-keywords.delete-keyword-notice') }}
            </div>
          </div>
        </template>
      </DeleteDialog>
    </div>
  </div>
</template>

<script>
import BaseButton from '@/components/base/BaseButton';
import ResizeAuto from '../page-admin-keywords/ResizeAuto.vue';
import BaseIcon from '@/components/base/BaseIcon';
import BaseRoundButton from '@/components/base/BaseRoundButton';
import FormCheckbox from '@/components/form/FormCheckbox.vue';
import Keyword from '@/components/page-keywords/Keyword';
import TableNormal from '@/components/table/TableNormal.vue';
import EditCategoryDialog from '../page-admin-keywords/EditCategoryDialog.vue';
import DeleteDialog from '../page-admin-keywords/DeleteDialog.vue';
import EditKeywordDialog from '../page-admin-keywords/EditKeywordDialog.vue';
import KeywordTransformButton from '../page-admin-keywords/KeywordTransformButton.vue';
import KeywordsAddPanel from '../page-admin-keywords/KeywordsAddPanel.vue';
import KeywordsPhoneCategory from '../page-admin-keywords/KeywordsPhoneCategory.vue';
import BaseShortcut from '../base/BaseShortcut.vue';
import { mapActions } from 'vuex';
import BaseBulkActionMenu from '../page-tenders/BaseBulkActionMenu.vue';
import { bus } from '@/main';
import { keywordsToAdd } from '@/util/keywords-util';
import { viewport } from '@/util/viewport';

// The documents API refuses more in one request
const MAX_BULK_KEYWORDS = 500;

export default {
  name: 'DocumentKeywordsList',
  components: {
    BaseButton,
    BaseIcon,
    BaseRoundButton,
    FormCheckbox,
    Keyword,
    BaseBulkActionMenu,
    TableNormal,
    ResizeAuto,
    EditCategoryDialog,
    DeleteDialog,
    EditKeywordDialog,
    KeywordTransformButton,
    KeywordsAddPanel,
    KeywordsPhoneCategory,
    BaseShortcut,
  },
  props: {
    category: {
      type: Object,
    },
  },
  data() {
    return {
      selectedKeywords: [],
      itemsShown: 8,
      showAll: false,
      showAddKeywordInput: false,
      rawKeywords: '',
      maxBulkKeywords: MAX_BULK_KEYWORDS,
    };
  },
  computed: {
    isPhone() {
      return viewport.size === 'phone';
    },
    keywords() {
      const categoryKeywords = this.category ? this.category.documentKeywords || [] : [];
      return categoryKeywords
        .filter((keyword) => keyword && keyword.value !== null && keyword.value.trim() !== '')
        .sort((a, b) => a.value.localeCompare(b.value));
    },
    itemsToShow() {
      return this.showAll ? -1 : this.itemsShown;
    },
    itemsLeft() {
      return this.keywords.length - this.itemsShown;
    },
    headers() {
      return [
        {
          text: this.$t('menu.keywords'),
          align: 'left',
          sortable: false,
          value: 'title',
          class: 'fixed-size-l',
        },
        {
          text: '',
          align: 'right',
          sortable: false,
          value: 'actions',
        },
      ];
    },
    hasSelection() {
      return this.selectedKeywords.length > 0;
    },
    allAreSelected() {
      return this.keywords.length === this.selectedKeywords.length;
    },
    partiallySelected() {
      return this.hasSelection && this.keywords.length !== this.selectedKeywords.length;
    },
    toAdd() {
      return keywordsToAdd(
        this.rawKeywords,
        this.keywords.map((keyword) => keyword.value),
      );
    },
    tooManyKeywords() {
      return this.toAdd.keywords.length > MAX_BULK_KEYWORDS;
    },
  },
  methods: {
    ...mapActions('documentKeywords', [
      'addDocumentKeywordsBulk',
      'deleteDocumentCategory',
      'deleteDocumentKeywords',
      'getKeywords',
    ]),
    toggleKeywordsList() {
      this.showAll = !this.showAll;
    },
    onCategoryEdit() {
      this.$refs.editCategoryDialog.openDialog(this.category);
    },
    onCategoryDelete() {
      this.$refs.deleteCategoryDialog.openDialog();
    },
    onKeywordsAdd() {
      const anchor = this.$refs.tendersBottomList;
      if (anchor) anchor.scrollIntoView({ block: 'center', behavior: 'smooth', inline: 'start' });
      this.showAddKeywordInput = true;
      this.$nextTick(() => {
        this.focusOnInput();
      });
    },
    addKeywords() {
      if (!this.toAdd.keywords.length || this.tooManyKeywords) return;

      return this.addDocumentKeywordsBulk({ categoryId: this.category.id, keywords: this.toAdd.keywords })
        .then(() => {
          this.rawKeywords = '';
          this.showAddKeywordInput = false;
          this.$toast.success(this.$t('page.admin-keywords.add-keywords-success'));
          this.onRefresh();
        })
        .catch((error) => this.$toast.error(error));
    },
    onDeleteCategory() {
      this.deleteDocumentCategory({ id: this.category.id })
        .then(() => {
          this.$toast.success(this.$t('page.admin-keywords.delete-category-success'));
          this.$emit('action-succeeded');
          this.onRefresh();
        })
        .catch((error) => this.$toast.error(error));
    },
    onKeywordsEdit(keyword) {
      this.$refs.editKeywordDialog.openDialog(keyword);
    },
    /**
     * Called after every successful keyword or category mutation. Reloads the keyword list and signals the
     * change so that views showing keyword matches (opportunity analysis, opportunity summary) can refetch
     * their match information: the matches of an opportunity are computed from all the keywords of the
     * tenant, so any change here makes the ones on screen outdated.
     */
    onRefresh() {
      this.getKeywords();
      bus.$emit('document-keywords-changed');
    },
    onSelectAllClick() {
      if (this.partiallySelected || this.allAreSelected) this.unselectAll();
      else this.selectAll();
    },
    selectAll() {
      this.selectedKeywords = [...this.keywords];
    },
    unselectAll() {
      this.selectedKeywords = [];
    },
    addTilde() {
      this.rawKeywords += '~';
      this.$nextTick(() => this.focusOnInput());
    },
    // On phones the box is in a panel, there once the panel opens (`opened`).
    focusOnInput() {
      const textArea = this.$refs.textAreaRef;
      if (textArea) textArea.focus();
    },
    openDeleteKeywords() {
      this.$refs.deleteKeywordsDialog.openDialog();
    },
    // Phones: the confirmation lists the keywords picked in the category (one, or a selection).
    onPhoneDelete(keywords) {
      this.selectedKeywords = keywords;
      this.openDeleteKeywords();
    },
    deleteKeywords() {
      const ids = this.selectedKeywords.map((kw) => kw.id);

      if (!ids.length) return;

      this.deleteDocumentKeywords({ categoryId: this.category.id, keywordIds: ids })
        .then(() => {
          this.$toast.success(this.$tc('page.admin-keywords.delete-keywords-success'));
          this.selectedKeywords = [];
          this.onRefresh();
        })
        .catch((error) => this.$toast.error(error));
    },
  },
};
</script>

<style lang="scss">
.document-keywords-body {
  width: 45%;
  margin-inline: 1.66%;

  // Phones: one category under the other, across the screen.
  @include screen-below('tablet') {
    width: 100%;
    margin-inline: 0;
  }
  .keywords-list__empty-keywords {
    display: flex;
    flex-direction: column;
  }
  .keywords-list__add-input {
    width: 100%;
  }
}
.document-keywords__add-keyword {
  margin-bottom: 20px;
}
.category-color {
  height: 16px;
  width: 16px;
  margin-right: 8px;
  margin-top: 8px;
  border-radius: $border-radius-large;
}
.page-keywords__add-keyword {
  display: flex;
  gap: 8px;
}
</style>
